import type { PortalSnapshotResponse } from "./types.js";

type PortalSnapshot = PortalSnapshotResponse["snapshot"];

export interface LiveSnapshotStabilizationState {
  primeRegressionCount: number;
  offlineCandidateSince: number | null;
  staleDetailsCandidateSince: number | null;
}

export const LIVE_OFFLINE_CONFIRMATION_MS = 30_000;

export const initialLiveSnapshotStabilizationState = (): LiveSnapshotStabilizationState => ({
  primeRegressionCount: 0,
  offlineCandidateSince: null,
  staleDetailsCandidateSince: null
});

export function stabilizeLiveSnapshot(
  current: PortalSnapshot,
  incoming: PortalSnapshot,
  state: LiveSnapshotStabilizationState,
  observedAt = Date.now()
): { snapshot: PortalSnapshot; state: LiveSnapshotStabilizationState } {
  const currentIsLive = current.liveDino.status.toLowerCase() === "live";
  const incomingIsOffline = incoming.liveDino.status.toLowerCase() === "offline";
  if (currentIsLive && incomingIsOffline) {
    const offlineCandidateSince = state.offlineCandidateSince ?? observedAt;
    if (observedAt - offlineCandidateSince < LIVE_OFFLINE_CONFIRMATION_MS) {
      return {
        snapshot: preserveTransientLiveDinosaur(current, incoming),
        state: { ...state, offlineCandidateSince }
      };
    }
  }

  const sameLiveSpecies = currentIsLive
    && incoming.liveDino.status.toLowerCase() === "live"
    && normalizeSpecies(current.liveDino.species) === normalizeSpecies(incoming.liveDino.species);
  if (sameLiveSpecies && incoming.liveDino.detailsFresh === false) {
    const staleDetailsCandidateSince = state.staleDetailsCandidateSince ?? observedAt;
    if (observedAt - staleDetailsCandidateSince < LIVE_OFFLINE_CONFIRMATION_MS) {
      return {
        snapshot: preserveStaleLiveTelemetry(current, incoming),
        state: { ...state, offlineCandidateSince: null, staleDetailsCandidateSince }
      };
    }
  }

  if (!isSameLiveDinosaur(current, incoming)) {
    return { snapshot: incoming, state: initialLiveSnapshotStabilizationState() };
  }

  const currentCount = current.primeChecklist?.completedCount ?? 0;
  const incomingCount = incoming.primeChecklist?.completedCount;

  if ((current.primeChecklist && !incoming.primeChecklist) || (current.enginePrime && !incoming.enginePrime)) {
    return {
      snapshot: preserveTransientLiveDetails(current, incoming),
      state: initialLiveSnapshotStabilizationState()
    };
  }

  if (incomingCount !== undefined && incomingCount < currentCount) {
    return {
      snapshot: preserveTransientLiveDetails(current, incoming),
      state: {
        primeRegressionCount: state.primeRegressionCount + 1,
        offlineCandidateSince: null,
        staleDetailsCandidateSince: null
      }
    };
  }

  return {
    snapshot: {
      ...incoming,
      skinCapture: incoming.skinCapture ?? current.skinCapture
    },
    state: initialLiveSnapshotStabilizationState()
  };
}

export function canReuseLiveDinoModel(currentSpecies: string | undefined, incomingSpecies: string): boolean {
  return Boolean(currentSpecies?.trim()) && normalizeSpecies(currentSpecies ?? "") === normalizeSpecies(incomingSpecies);
}

export function canReuseLiveDinoCard(
  currentSpecies: string | undefined,
  currentStatus: string | undefined,
  incomingSpecies: string,
  incomingStatus: string
): boolean {
  return canReuseLiveDinoModel(currentSpecies, incomingSpecies)
    && currentStatus?.trim().toLowerCase() === incomingStatus.trim().toLowerCase();
}

function preserveTransientLiveDetails(current: PortalSnapshot, incoming: PortalSnapshot): PortalSnapshot {
  return {
    ...incoming,
    primeChecklist: current.primeChecklist,
    enginePrime: current.enginePrime,
    skinCapture: incoming.skinCapture ?? current.skinCapture
  };
}

function preserveStaleLiveTelemetry(current: PortalSnapshot, incoming: PortalSnapshot): PortalSnapshot {
  return {
    ...preserveTransientLiveDetails(current, incoming),
    liveDino: {
      ...incoming.liveDino,
      uid: current.liveDino.uid,
      species: current.liveDino.species,
      growth: current.liveDino.growth,
      health: current.liveDino.health,
      stamina: current.liveDino.stamina,
      hunger: current.liveDino.hunger,
      thirst: current.liveDino.thirst,
      primeActive: current.liveDino.primeActive,
      detailsCapturedAt: current.liveDino.detailsCapturedAt
    }
  };
}

function preserveTransientLiveDinosaur(current: PortalSnapshot, incoming: PortalSnapshot): PortalSnapshot {
  return {
    ...incoming,
    liveDino: current.liveDino,
    primeChecklist: current.primeChecklist,
    enginePrime: current.enginePrime,
    skinCapture: incoming.skinCapture ?? current.skinCapture
  };
}

function isSameLiveDinosaur(current: PortalSnapshot, incoming: PortalSnapshot): boolean {
  if (current.liveDino.status.toLowerCase() !== "live" || incoming.liveDino.status.toLowerCase() !== "live") return false;
  if (normalizeSpecies(current.liveDino.species) !== normalizeSpecies(incoming.liveDino.species)) return false;
  if (current.liveDino.uid !== incoming.liveDino.uid) return false;
  if (incoming.liveDino.detailsFresh === false) return true;
  return incoming.liveDino.growth + 5 >= current.liveDino.growth;
}

function normalizeSpecies(value: string): string {
  const leaf = value.trim().split(/[\\/]/).at(-1) ?? value;
  return leaf
    .replace(/^BP_/i, "")
    .replace(/(?:\.[^.]+)?_C$/i, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}
